import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Headphones, ChevronRight } from 'lucide-react';
import { IconButton } from '@/components/atoms/IconButton';
import { ROUTES } from '@/constants/routes';
import type { StoryType } from '@/types/backoffice.types';
import styles from './StoryTypeSelectorPage.module.scss';

interface StoryTypeOption {
  type: StoryType;
  icon: React.ReactNode;
  label: string;
  description: string;
  details: string[];
  isNew?: boolean;
}

const STORY_TYPE_OPTIONS: StoryTypeOption[] = [
  {
    type: 'general',
    icon: <FileText size={32} />,
    label: 'General',
    description: 'Historias de comprensión lectora, vocabulario y gramática.',
    details: [
      'Recursos de texto, audio y vídeo',
      'Todos los tipos de actividad disponibles',
      'Adecuado para cualquier habilidad lingüística',
    ],
  },
  {
    type: 'listening',
    icon: <Headphones size={32} />,
    label: 'Listening',
    description: 'Historias de comprensión auditiva basadas en audio.',
    details: [
      'Requiere al menos un recurso de audio',
      'El audio debe incluir transcript',
      'Diseñado para practicar la escucha activa',
    ],
    isNew: true,
  },
];

export function StoryTypeSelectorPage() {
  const navigate = useNavigate();

  const handleSelectType = (type: StoryType) => {
    navigate(`${ROUTES.BACKOFFICE.STORY_CREATE}?type=${type}`);
  };

  return (
    <div className={styles.typeSelector}>
      <div className={styles.typeSelector__header}>
        <IconButton
          icon={ArrowLeft}
          onClick={() => navigate(ROUTES.BACKOFFICE.STORIES)}
          aria-label="Volver a Historias"
        />
      </div>

      <div className={styles.typeSelector__intro}>
        <h1 className={styles.typeSelector__title}>Crear historia</h1>
        <p className={styles.typeSelector__subtitle}>
          Selecciona el tipo de historia que quieres crear
        </p>
      </div>

      <div className={styles.typeSelector__grid}>
        {STORY_TYPE_OPTIONS.map((option) => (
          <button
            key={option.type}
            type="button"
            className={styles.typeSelector__card}
            data-no-press-scale=""
            onClick={() => handleSelectType(option.type)}
            aria-label={`Crear historia ${option.label}`}
          >
            <div className={styles.typeSelector__cardHeader}>
              <span className={styles.typeSelector__cardIcon}>{option.icon}</span>
              <div className={styles.typeSelector__cardTitleRow}>
                <h2 className={styles.typeSelector__cardTitle}>{option.label}</h2>
                {option.isNew && (
                  <span className={styles.typeSelector__newBadge} aria-label="Nueva función">
                    New
                  </span>
                )}
              </div>
            </div>

            <p className={styles.typeSelector__cardDescription}>{option.description}</p>

            <ul className={styles.typeSelector__cardDetails} aria-label={`Características ${option.label}`}>
              {option.details.map((detail) => (
                <li key={detail} className={styles.typeSelector__cardDetail}>
                  {detail}
                </li>
              ))}
            </ul>

            <span className={styles.typeSelector__cardAction} aria-hidden="true">
              <ChevronRight size={20} />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
